import React, { useState, useEffect, useContext, useLayoutEffect } from "react";
import { OAuth2, AuthSidebar } from "components";
import ReCAPTCHA from "react-google-recaptcha";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Container,
  Grid,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslations, useForm } from "hooks";
import { getTranslation, CONSTANTS } from "common";
import { noTokenApi, usersApi, fetchMe } from "api";
import { isEmail, isRequired, Validation } from "hooks/useForm";
import { AuthContext } from "contexts";
import { noSpecialChars } from "common/utilities";
import { Step } from "components/LandingSidebar";
import FlowentiButton from "components/flowenti-ui-kit/FlowentiButton";

interface LoginProps {
  login?: boolean;
  onGoogleConfirm?: (token: string, registrated: boolean) => Promise<void>;
  showLoginButton?: boolean; //per non far vedere "duplicati" del bottone login
}

const Login = ({ login = true, onGoogleConfirm, showLoginButton = true }: LoginProps) => {
  const [searchParams] = useSearchParams();
  const { fromRegister } = useLocation().state || {};
  const [loading, setLoading] = useState<boolean>(false);
  const emailParam = searchParams.get("email");
  const translations = useTranslations();
  // const user = useUser();
  const {
    actions: { setUser, setRegisterUser },
  } = useContext(AuthContext);
  const theme = useTheme();
  // const dispatch = useDispatch();
  const navigate = useNavigate();
  const [exist, setExist] = useState<boolean>(false);
  const [loginError, setLoginError] = useState<boolean>(false);
  const isE2E = Boolean(window.Cypress) || Boolean((window as any).Playwright);
  const shouldRequireCaptcha = process.env.REACT_APP_ENV === "production" && !isE2E;
  const loginTextFieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "background.paper",
    },
    "& input:-webkit-autofill": {
      WebkitBoxShadow: (theme: any) => `0 0 0 100px ${theme.palette.background.paper} inset`,
      WebkitTextFillColor: (theme: any) => theme.palette.text.primary,
      caretColor: (theme: any) => theme.palette.text.primary,
      transition: "background-color 5000s ease-in-out 0s",
    },
  };

  // useEffect(() => {
  //   if (!user) {
  //     dispatch(resetOrganizationState());
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [user]);

  useEffect(() => {
    (async () => {
      if (emailParam) {
        const response = await usersApi.get(
          `emailinplatform/${emailParam.toLowerCase()}`
        );
        setExist(response.data.mailexist);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validations: Validation[] = [
    ({ email }) =>
      isRequired(email) || {
        email: getTranslation(translations, "register.valid.Error_after_at"),
      },
    ({ captcha }) =>
      isRequired(captcha) ||
      ["development", "test"].includes(process.env.REACT_APP_ENV || "") ||
      isE2E || {
        captcha: getTranslation(translations, "login.valid.captchaError"),
      },
    ({ email }) =>
      isEmail(email) || {
        email: getTranslation(translations, "register.valid.Error_after_at"),
      },
    ({ email }) =>
      noSpecialChars(email) || {
        email: getTranslation(
          translations,
          "register.valid.Error_invalid_chars"
        ),
      },
  ];

  const { values, changeHandler, errors, touched, isValid } = useForm(
    {
      email: emailParam ?? "",
      password: "",
      captcha: null,
    },
    validations
  );

  const enterWithGoogle = async (token: string, registrated: boolean) => {
    try {
      sessionStorage.setItem(CONSTANTS.STORE_JWT, token);
      let result = await fetchMe(token);
      if (registrated) {
        setUser({
          jwt: token,
          userId: result.data.user.id,
          email: result.data.user.email,
          username: result.data.user.username,
          firstname: result.data.user.firstname,
          lastname: result.data.user.lastname,
          picture: result.data.user.picture,
          linkprofile: result.data.user.linkprofile,
          subscriptiontype: result.data.user.subscriptiontype,
          i18n: result.data.user.i18n,
          dob: result.data.user.dob,
          authenticationtype: result.data.user.authenticationtype,
        });
        navigate("/dashboard/organizations");
      } else {
        setRegisterUser({
          userId: result.data.user.id,
          jwt: token,
          ...result.data.user,
        });
        navigate("/organization");
      }
    } catch {
    } finally {
    }
  };

  const [autofilled, setAutofilled] = useState<boolean | undefined>();

  useEffect(() => {
    if (autofilled && (touched.email || touched.password || touched.captcha)) {
      setAutofilled(undefined);
    }
  }, [touched, autofilled]);

  useLayoutEffect(() => {
    /**
     * The field can be prefilled on the very first page loading by the browser
     * By the security reason browser limits access to the field value from JS level and the value becomes available
     * only after first user interaction with the page
     * So, even if the Formik thinks that the field is not touched by user and empty,
     * it actually can have some value, so we should process this edge case in the form logic
     */
    const checkAutofilled = () => {
      const autofilled = !!document
        .getElementById("password-login")
        ?.matches("*:-webkit-autofill");
      if (autofilled) {
        setAutofilled(true);
      }
    };
    setTimeout(checkAutofilled, 500);
    setTimeout(checkAutofilled, 1000);
    setTimeout(checkAutofilled, 2000);
  }, []);

  const submitData = async (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    if (isValid) {
      event.preventDefault();
      setLoading(true);
      const { email, password, captcha } = values;
      if (
        !captcha &&
        shouldRequireCaptcha
      )
        return;
      try {
        const response = await noTokenApi.post("login", {
          jwtusername: email.toLowerCase(),
          jwtpassword: password,
        });
        const token = response.data.token;
        sessionStorage.setItem(CONSTANTS.STORE_JWT, token);
        let result = await fetchMe(token);
        setUser({
          jwt: token,
          userId: result.data.user.id,
          email: result.data.user.email,
          username: result.data.user.username,
          firstname: result.data.user.firstname,
          lastname: result.data.user.lastname,
          picture: result.data.user.picture,
          linkprofile: result.data.user.linkprofile,
          subscriptiontype: result.data.user.subscriptiontype,
          i18n: result.data.user.i18n,
          dob: result.data.user.dob,
          authenticationtype: result.data.user.authenticationtype,
        });
        navigate("/dashboard/organizations");
      } catch {
        setLoginError(true);
      } finally {
      }
      setLoading(false);
    }
  };

  const steps: Step[] = [
    {
      number: 1,
      title: getTranslation(translations, "step_title.configure_organization"),
      description: getTranslation(translations, "step_description.configure_organization"),
    },
    {
      number: 2,
      title: getTranslation(translations, "step_title.design_and_publish"),
      description: getTranslation(translations, "step_description.design_and_publish"),
    },
    {
      number: 3,
      title: getTranslation(translations, "step_title.start_and_collaborate"),
      description: getTranslation(translations, "step_description.start_and_collaborate")
    },
    {
      number: 4,
      title: getTranslation(translations, "step_title.monitor_and_analyze"),
      description: getTranslation(translations, "step_description.monitor_and_analyze"),
    },
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        background: "background.default",
        flexDirection: { xs: "column-reverse", md: "row" },
        // mr: { xs: 2, md: 46 }
      }}
    >
      <AuthSidebar
        title={getTranslation(translations, "how_it_works.title")}
        steps={steps}
      />

      {/* Right Section - Login Form */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 4,
          background: "background.paper",
        }}
      >
        <Container maxWidth="sm">
          <Box sx={{ maxWidth: 480, mx: "auto" }}>
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 700,
                mb: 1,
                textAlign: "center",
              }}
            >
              {getTranslation(translations, "register.signin.label")}
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                mb: 4,
                textAlign: "center",
              }}
            >
              {getTranslation(translations, "login.label.welcome_back")}
            </Typography>

            {/* Alerts */}
            <Stack spacing={2} sx={{ mb: 3 }}>
              {exist && (
                <Alert variant="outlined" color="info">
                  {getTranslation(
                    translations,
                    "register.label.email_is_present_on_platform"
                  )}
                </Alert>
              )}
              {fromRegister && (
                <Alert variant="outlined" color="info">
                  {getTranslation(translations, "register.label.checkyouremail")}
                </Alert>
              )}
              {loginError && (
                <Alert variant="outlined" severity="error" >
                  {getTranslation(translations, "login.error.invalid_credentials")}
                </Alert>
              )}
            </Stack>
            {/* {process.env.REACT_APP_ENV === "development" && */}
            <>
              <OAuth2 login onGoogleConfirm={enterWithGoogle} />

              <Box sx={{ position: "relative", my: 3 }}>
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: 0,
                    right: 0,
                    height: "1px",
                    backgroundColor: "divider",
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    position: "relative",
                    textAlign: "center",
                    color: "text.secondary",
                    backgroundColor: "background.paper",
                    display: "inline-block",
                    px: 2,
                    left: "50%",
                    transform: "translateX(-50%)",
                  }}
                >
                  {getTranslation(translations, "login.label.or_login_with_email")}
                </Typography>
              </Box>
            </>
            {/* } */}

            <Grid container component="form" spacing={2}>
              <Grid item xs={12}>
                <TextField
                  id="email-login"
                  size="small"
                  type="email"
                  label={getTranslation(translations, "user.email.label")}
                  InputLabelProps={{ shrink: autofilled }}
                  autoComplete="off"
                  fullWidth
                  value={values.email}
                  sx={loginTextFieldSx}
                  helperText={touched.email && errors.email}
                  error={Boolean(touched.email && errors.email)}
                  onChange={({ target }) => {
                    setLoginError(false)
                    changeHandler("email", target.value);
                  }}
                />
              </Grid>
              <Grid item xs={12} mt={1.5}>
                <TextField
                  id="password-login"
                  size="small"
                  type="password"
                  label="password"
                  InputLabelProps={{ shrink: autofilled }}
                  autoComplete="off"
                  fullWidth
                  value={values.password}
                  sx={loginTextFieldSx}
                  helperText={touched.password && errors.password}
                  error={Boolean(touched.password && errors.password)}
                  onChange={({ target }) => {
                    setLoginError(false);
                    changeHandler("password", target.value);
                  }
                  }
                />
              </Grid>

              {/* ReCAPTCHA */}
              {shouldRequireCaptcha && (
                <Grid item xs={12}>
                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <ReCAPTCHA
                      id="recaptcha"
                      theme={theme.palette.mode}
                      onChange={(_: any) => changeHandler("captcha", _)}
                      sitekey="6LeTBVspAAAAAPds-GYTsoJaZJ3ean2pXKNNX2A6"
                    />
                  </Box>
                </Grid>
              )}
              <Grid item xs={12}>
                <FlowentiButton
                  id="button-login"
                  variant="contained"
                  fullWidth
                  type="submit"
                  disabled={
                    loading ||
                    !values.email ||
                    !values.password ||
                    (shouldRequireCaptcha && !values.captcha)
                  } onClick={submitData}
                >
                  {getTranslation(translations, "register.signin.label")}
                </FlowentiButton>
              </Grid>
            </Grid>
          </Box>
          <React.Fragment>
            {login ? (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", mt: 4, gap: 6 }}>
                <FlowentiButton
                  component={Link}
                  to="/registration"
                  variant="contained"
                //  variant="text"
                // color="inherit"
                >
                  {getTranslation(
                    translations,
                    "register.label.register_an_account"
                  )}
                </FlowentiButton>
                <FlowentiButton
                  id="forgot-password-btn"
                  component={Link}
                  to="/forgotpassword"
                  variant="outlined"
                // variant="text"
                // color="inherit"
                >
                  {getTranslation(translations, "register.forgotpassword.label")}
                </FlowentiButton>
              </Box>
            ) : (
              showLoginButton && ( //così riusciamo a gestire meglio la visualizzazione del testo, abbiamo l'esempio in email.tsx
                <FlowentiButton component={Link} to="/login" variant="text" color="inherit">
                  {getTranslation(
                    translations,
                    "register.label.ahref_login_have_account"
                  )}
                </FlowentiButton>
              )
            )}
          </React.Fragment>
        </Container>
      </Box>
    </Box>
  );
};

export default Login;
